import { Link } from 'react-router-dom'
import { useContactForm } from '@/hooks'
import { Input, Textarea, Select, Button } from '@/components/ui'
import { SEO } from '@/components/seo'

const timeOptions = [
  { value: '09:00', label: '09:00 AM' },
  { value: '09:30', label: '09:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '12:30', label: '12:30 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '13:30', label: '1:30 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '14:30', label: '2:30 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '15:30', label: '3:30 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '16:30', label: '4:30 PM' },
  { value: '17:00', label: '5:00 PM' },
]

const demoFeatures = [
  {
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    text: '30-minute personalised session',
  },
  {
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    text: '1-on-1 with a product expert',
  },
  {
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    text: 'Screen share & live Q&A',
  },
  {
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    text: 'Tailored to your practice',
  },
]

export default function BookDemoPage() {
  const { formData, isSubmitting, isSubmitted, error, handleChange, handleSubmit } =
    useContactForm('demo')

  const today = new Date().toISOString().split('T')[0]

  return (
    <>
      <SEO
        title="Book a Demo - WealthSpace | AI Practice Management"
        description="Book a personalised WealthSpace demo. See how AI can transform your financial advice practice in just 30 minutes."
        keywords="WealthSpace demo, financial adviser software demo, AI practice management demo, book demo, WealthSpace trial"
        url="/book-demo"
      />

      {/* Hero */}
      <section className="pt-[60px] pb-10 px-6 relative z-[1]">
        <div className="max-w-[800px] mx-auto text-center animate-fade-in-up opacity-0 [animation-delay:0.1s] [animation-fill-mode:forwards]">
          <p className="text-sm font-semibold text-accent-teal tracking-[0.1em] uppercase mb-4">
            30-minute personalised demo
          </p>
          <h1 className="font-serif text-[clamp(32px,5vw,48px)] font-normal leading-[1.2] tracking-[-0.02em] mb-4">
            Book a <span className="text-accent-teal">call</span>
          </h1>
          <p className="text-[17px] text-brand-muted leading-[1.6] max-w-[500px] mx-auto">
            Choose a time that works for you. We'll tailor the demo to your practice.
          </p>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="px-6 pb-20 relative z-[1]">
        <div className="max-w-[520px] mx-auto animate-fade-in-up opacity-0 [animation-delay:0.2s] [animation-fill-mode:forwards]">
          <div className="bg-[linear-gradient(145deg,rgba(255,255,255,0.03)_0%,rgba(255,255,255,0.01)_100%)] border border-white/[0.06] rounded-2xl p-6 md:p-8">
            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-accent-teal/20 flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-8 h-8 text-accent-teal"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2 className="font-serif text-[28px] text-brand-light mb-3">You're booked!</h2>
                <p className="text-brand-muted leading-relaxed mb-6">
                  We've received your request and will send a calendar invite to confirm your
                  preferred time, or suggest an alternative if needed.
                </p>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4 text-left">
                  <p className="text-sm text-brand-muted mb-2">
                    <span className="text-brand-subtle">Preferred date:</span>{' '}
                    {formData.preferredDate}
                  </p>
                  <p className="text-sm text-brand-muted">
                    <span className="text-brand-subtle">Preferred time:</span>{' '}
                    {formData.preferredTime} UK
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-white/[0.06]">
                  <p className="text-sm text-brand-subtle mb-3">While you wait...</p>
                  <div className="flex flex-col gap-2">
                    <Link
                      to="/features"
                      className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg text-sm text-brand-muted hover:bg-white/[0.04] hover:border-white/10 transition-all"
                    >
                      <svg
                        className="w-5 h-5 text-accent-teal"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M9 9h6M9 13h6M9 17h4" />
                      </svg>
                      Explore all features
                    </Link>
                    <Link
                      to="/demo"
                      className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg text-sm text-brand-muted hover:bg-white/[0.04] hover:border-white/10 transition-all"
                    >
                      <svg
                        className="w-5 h-5 text-accent-teal"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      Try our interactive demo
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Features Banner */}
                <div className="grid grid-cols-2 gap-3 mb-6 pb-6 border-b border-white/[0.06]">
                  {demoFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-brand-muted">
                      <span className="text-accent-teal">{feature.icon}</span>
                      <span className="text-[13px]">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                {/* Honeypot field */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="space-y-4">
                  <Input
                    label="Your name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Email address"
                    name="email"
                    type="email"
                    placeholder="john@company.co.uk"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Company name"
                    name="company"
                    type="text"
                    placeholder="Acme Financial Planning"
                    value={formData.company}
                    onChange={handleChange}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Preferred Date"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={today}
                      required
                    />
                    <Select
                      label="Preferred Time (UK)"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      options={timeOptions}
                      placeholder="Select a time..."
                      required
                    />
                  </div>

                  <Textarea
                    label="What would you like to see?"
                    name="message"
                    placeholder="Tell us about your practice, any specific features you'd like to explore, or questions you have..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full justify-center mt-6" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Booking...
                    </>
                  ) : (
                    <>
                      Confirm Booking
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </Button>

                <p className="text-[13px] text-brand-subtle text-center mt-4">
                  We'll confirm within 24 hours. Usually much sooner.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="text-center px-6 pb-16 relative z-[1]">
        <Link
          to="/get-started"
          className="inline-flex items-center gap-2 text-sm text-brand-subtle hover:text-brand-muted transition-colors"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to options
        </Link>
      </div>
    </>
  )
}
